/* eslint-disable max-len */
import { useCookies } from 'react-cookie';
import { useDispatch, useSelector } from 'react-redux';
import { withTheme } from 'styled-components';
import { Fragment, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { deleteTeamMemberAdmin, getTeamsAdmin, getTeamsIdAdmin, getUserAdmin, upsertTeamMemberAdmin } from 'store/actions/admin';
import { MemberContainer, AddTeamButton, IconButton, TitleContainer, TabContainer, Tab, SortContainer, HeaderContainer, EmptyStateContainer } from './styles';
import { listColumns, teamMembersListColumns } from './listColumns';
import { AddMemberForm } from './AddMemberForm';
import { AddTeamForm } from './AddTeamForm';
import { AddCreditsForm } from '../Users/AddCreditsForm';
import { Settings } from './Settings';
import {
    FovLoader,
    VerticalSpacer,
    HorizontalSpacer,
    PlusOutlined,
    H4,
    FovDivider,
    H5,
    FovConfirmation,
    Body,
    ArrowLeftOutlined,
    FovSelect,
    FovOption,
    SearchInputField,
} from 'components';
const AdminTeams = withTheme(props => {
    const { theme } = props;
    const dispatch = useDispatch();

    const userDataStore = useSelector(state => state.userData);
    const adminDataStore = useSelector(state => state.adminData);

    const [ cookies ] = useCookies([ 'userAuth' ]);
    const token = cookies.archvision_token;

    const selectedTheme = userDataStore.userPreferences.theme;
    const teams = useMemo(() => adminDataStore.teams, [ adminDataStore.teams ]);
    const selectedTeamData = useMemo(() => adminDataStore.selectedTeamData, [ adminDataStore.selectedTeamData ]);
    const isLoading = useMemo(() => adminDataStore.isLoading, [ adminDataStore.isLoading ]);
    const pagination = useMemo(() => adminDataStore.pagination, [ adminDataStore.pagination ]);
    const { page, pageSize, totalCount } = pagination;

    const searchFieldRef = useRef(null);
    const [ selectedTeam, setSelectedTeam ] = useState(null);
    const [ showAddTeamForm, setShowAddTeamForm ] = useState(false);
    const [ showAddTeamMemberForm, setShowAddTeamMemberForm ] = useState(false);
    const [ selectedTeamMember, setSelectedTeamMember ] = useState(null);
    const [ showMemberDelete, setShowMemberDelete ] = useState(false);
    const [ isDeleting, setIsDeleting ] = useState(false);
    const [ selectedTab, setSelectedTab ] = useState('members');
    const [ membersData, setMembersData ] = useState({});
    const [ creditsMember, setCreditsMember ] = useState(null);
    const [ searchParams, setSearchParams ] = useSearchParams();

    const sortedUsers = selectedTeamData?.users?.toSorted((u1,u2) => {
        const [ f1, l1 ] = [ u1.firstName, u1.lastName ];
        const [ f2, l2 ] = [ u2.firstName, u2.lastName ];
        if (l1.localeCompare(l2) === 0) return f1.localeCompare(f2);
        return l1.localeCompare(l2);
    });

    const handlePageChange = page => {
        searchParams.set('page', page);
        setSearchParams(searchParams);
    };

    const handleSearchBlur = () => {
        const value = searchFieldRef.current.value;
        if (value === '') {
            searchParams.delete('search');
        } else {
            searchParams.set('search', value);
        }
        searchParams.set('page', 1);
        setSearchParams(searchParams);
        dispatch(getTeamsAdmin(token));
    };

    useEffect(() => {
        if(!teams || teams.length === 0) {
            dispatch(getTeamsAdmin(token));
        }
    }, []);

    useEffect(() => {
        if (selectedTeam) {
            dispatch(getTeamsIdAdmin(token, selectedTeam.team_id));
        }
    }, [ selectedTeam ]);

    useEffect(() => {
        const users = selectedTeamData?.users;
        if (!users?.length) return;

        let cancelled = false;
        Promise.all(users.map(member => dispatch(getUserAdmin(token, member.email))))
            .then(results => {
                if (cancelled) return;
                const map = {};
                results.forEach(user => {
                    if (user) map[user.id] = user;
                });
                setMembersData(map);
            });

        return () => { cancelled = true; };
    }, [ selectedTeamData ]);

    const handleCreditsClose = () => {
        const memberEmail = creditsMember?.email;
        setCreditsMember(null);
        if (memberEmail) {
            dispatch(getUserAdmin(token, memberEmail)).then(user => {
                if (user) setMembersData(prev => ({ ...prev, [user.id]: user }));
            });
        }
    };

    const handleMemberRoleUpdate = useCallback((userId, role) => {
        dispatch(upsertTeamMemberAdmin(token, selectedTeam.team_id, [ { id: userId, role } ]));
    }, [ selectedTeam ]);

    const handleDeleteMember = useCallback(userId => {
        const onComplete = () => {
            setShowMemberDelete(false);
            setIsDeleting(false);
        };

        dispatch(deleteTeamMemberAdmin(token, selectedTeam.team_id, [ { id: userId } ], onComplete));
    }, [ selectedTeam ]);

    const tabIsSelected = name => selectedTab === name;

    const sortOptions = [
        { key: 'NAME_ASC', text: 'Name (A-Z)' },
        { key: 'NAME_DESC', text: 'Name (Z-A)' },
        { key: 'USER_COUNT_ASC', text: 'User Count ↑' },
        { key: 'USER_COUNT_DESC', text: 'User Count ↓' },
    ];
    const buildSortOptions = () => sortOptions.map(option => (
        <FovOption key={option.key} value={option.key}>
            {option.text}
        </FovOption>
    ));

    return (
        <FovLoader
            text='Retrieving teams'
            resource={!isLoading || selectedTeam}
        >
            {teams?.length === 0 && !searchParams.get('search') && !selectedTeam && <div>No teams.</div>}
            {!selectedTeam && (teams?.length > 0 || searchParams.get('search')) &&
                <Fragment>
                    <HeaderContainer>
                        <SearchInputField
                            width={400}
                            disabled={false}
                            ref={searchFieldRef}
                            defaultValue={searchFieldRef.current?.value || searchParams.get('search')}
                            onChange={e => searchFieldRef.current.value = e.target.value}
                            onKeyUp={e => e.key === 'Enter' && e.target.blur()}
                            onBlur={handleSearchBlur}
                        />
                        <SortContainer>
                            <FovSelect
                                value={searchParams.get('order_by') || ''}
                                placeholder={'Sort by'}
                                onChange={(_e, newVal) => {
                                    if (!searchParams.has('order_by') || (searchParams.get('order_by') !== newVal)) {
                                        searchParams.set('order_by', newVal);
                                        searchParams.set('page', 1);
                                        setSearchParams(searchParams);
                                        dispatch(getTeamsAdmin(token));
                                    }
                                }}
                            >
                                {buildSortOptions()}
                            </FovSelect>
                        </SortContainer>
                        <HorizontalSpacer size={12} />
                        <AddTeamButton
                            $selectedTheme={selectedTheme}
                            onClick={() => {
                                setShowAddTeamForm(true);
                            }}
                        >
                            <>Add Team</>
                            <HorizontalSpacer size={6} />
                            <PlusOutlined width={20}/>
                        </AddTeamButton>
                    </HeaderContainer>
                    <VerticalSpacer size={5} />
                    {teams?.length === 0
                        ? (
                            <EmptyStateContainer>
                                <Body size='Medium'>
                                    {`No matching teams found for "${searchParams.get('search')}"`}
                                </Body>
                            </EmptyStateContainer>
                        )
                        : (
                            <MemberContainer
                                dataSource={teams}
                                $selectedTheme={selectedTheme}
                                columns={listColumns(theme, selectedTheme, setSelectedTeam)}
                                rowKey={'row-' + Math.random()}
                                currentPage={page}
                                pageSize={pageSize}
                                totalCount={totalCount}
                                isPagination={true}
                                selectedKey='team_id'
                                selectedGuid={selectedTeam?.team_id}
                                onPageChange={handlePageChange}
                            />
                        )
                    }
                    <AddTeamForm
                        isVisible={showAddTeamForm}
                        onClose={() => setShowAddTeamForm(false)}
                    />
                </Fragment>
            }
            {selectedTeam &&
                <FovLoader resource={!isLoading} text={'Retrieving team data'}>
                    <TitleContainer>
                        <H4>{selectedTeamData.display_name} ({selectedTeamData.team_id})</H4>
                        <IconButton
                            theme={theme}
                            $selectedTheme={selectedTheme}
                            backgroundColor={'onPrimary'}
                            hoverColor={theme.colors[selectedTheme].accentHover}
                            onClick={() => setSelectedTeam(null)}
                            icon={
                                <ArrowLeftOutlined
                                    theme={theme}
                                    color={theme.colors[selectedTheme].primary}
                                />
                            }
                        />
                    </TitleContainer>
                    <TabContainer>
                        <Tab
                            theme={theme}
                            $selectedTheme={selectedTheme}
                            onClick={() => {
                                setSelectedTab('members');
                            }}
                            selected={tabIsSelected('members')}
                        >
                            Members
                        </Tab>
                        <Tab
                            theme={theme}
                            $selectedTheme={selectedTheme}
                            onClick={() => {
                                setSelectedTab('settings');
                            }}
                            selected={tabIsSelected('settings')}
                        >
                            Settings
                        </Tab>
                    </TabContainer>
                    <FovDivider />
                    <VerticalSpacer size={16}/>

                    { // TODO: Make members its own component like Settings
                    selectedTab === 'members' &&
                        <Fragment>
                            <TitleContainer>
                                <H5>Members</H5>
                                <AddTeamButton
                                    $selectedTheme={selectedTheme}
                                    onClick={() => setShowAddTeamMemberForm(true)}
                                >
                                    <>Add Member</>
                                    <HorizontalSpacer size={6} />
                                    <PlusOutlined width={20}/>
                                </AddTeamButton>
                            </TitleContainer>
                            <VerticalSpacer size={5} />
                            <MemberContainer
                                dataSource={sortedUsers}
                                $selectedTheme={selectedTheme}
                                columns={teamMembersListColumns(theme, selectedTheme, handleMemberRoleUpdate, setSelectedTeamMember, setShowMemberDelete, membersData, setCreditsMember)}
                                rowKey={'row-' + Math.random()}
                                selectedKey='userId'
                                selectedGuid={selectedTeamMember?.userId}
                                totalCount={sortedUsers?.length || 0}
                            />
                            <VerticalSpacer size={15} />
                        </Fragment>
                    }
                    {selectedTab === 'settings' && <Settings />}
                    <FovConfirmation
                        onConfirm={() => {
                            setIsDeleting(true);
                            handleDeleteMember(selectedTeamMember.userId);
                        }}
                        onClose={() => {
                            setShowMemberDelete(false);
                        }}
                        confirmationText='Remove member'
                        isOpened={showMemberDelete}
                        ignoreConfirmClose={true}
                        confirmColor={theme.colors[selectedTheme].error}
                        confirmHoverColor={theme.colors[selectedTheme].errorHover}
                        disabled={isDeleting}
                    >
                        <Body size='Medium'>
                            {
                            `Are you sure you want to remove ${selectedTeamMember?.email || 'selected member'} from the team?`
                            }
                        </Body>
                    </FovConfirmation>
                    <AddMemberForm
                        isVisible={showAddTeamMemberForm}
                        onClose={() => setShowAddTeamMemberForm(false)}
                        allowedDomains={selectedTeam?.allowed_domains}
                        teamId={selectedTeam?.team_id}
                    />
                    <AddCreditsForm
                        isVisible={!!creditsMember}
                        user={creditsMember}
                        onClose={handleCreditsClose}
                    />
                </FovLoader>
            }
        </FovLoader>
    );
});

export default AdminTeams;
